import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Minus, Plus, Check, NotebookPen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ReportDueCard } from './WeeklyReportPage';
import { ClientShell } from '../components/ClientShell';
import {
  useClientData, todayISO, activePlanOf, nextWorkoutDay, dayLabel, MOODS, moodToValue, moodLabel,
} from '../hooks/useClientData';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CheckIn = ({ todayEntry, onSaved }) => {
  const [mood, setMood] = useState(null);
  const [pain, setPain] = useState(0);
  const [sleep, setSleep] = useState(7.5);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(!todayEntry);

  useEffect(() => { setEditing(!todayEntry); }, [todayEntry]);

  const save = async () => {
    if (mood == null) { toast.error('Wybierz, jak się czujesz.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/client/my-monitoring`, {
        log_date: todayISO(),
        mood: moodToValue(mood),
        pain_level: pain,
        sleep_hours: sleep,
      }, { withCredentials: true });
      toast.success('Zapisane. Dzięki!');
      onSaved();
    } catch (error) {
      console.error('Error saving check-in:', error);
      toast.error('Nie udało się zapisać. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <section className="glass rounded-[28px] p-6 sm:p-9 flex flex-col gap-5" data-testid="checkin-done">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">Dzisiejszy wpis</h2>
          <span className="chip"><Check className="w-3.5 h-3.5" /> Zapisany</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
          {[
            ['Samopoczucie', moodLabel(todayEntry.mood) || '—'],
            ['Ból', todayEntry.pain_level != null ? `${todayEntry.pain_level}/10` : '—'],
            ['Sen', todayEntry.sleep_hours != null ? `${String(todayEntry.sleep_hours).replace('.', ',')} h` : '—'],
          ].map(([l, v]) => (
            <div key={l} className="glass-soft rounded-2xl px-4 py-3 sm:p-4 flex sm:block items-center justify-between min-w-0">
              <p className="label-text text-[var(--text-muted)] text-[0.55rem] !tracking-[0.12em]">{l}</p>
              <p className="big-num text-2xl sm:mt-2 truncate">{v}</p>
            </div>
          ))}
        </div>
        <button onClick={() => setEditing(true)} className="text-sm text-[var(--text-muted)] self-start hover:text-[var(--text-primary)]">
          Dodaj nowy wpis
        </button>
      </section>
    );
  }

  return (
    <section className="glass rounded-[28px] p-6 sm:p-9 flex flex-col gap-7" data-testid="checkin-form">
      <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">Jak się dziś czujesz?</h2>

      <div className="flex flex-col gap-3">
        <p className="label-text text-[var(--text-muted)]">Samopoczucie</p>
        <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
          {MOODS.map((m, i) => (
            <button key={m} onClick={() => setMood(i)} aria-pressed={mood === i}
              className={`h-14 rounded-2xl text-[11px] sm:text-[13px] font-semibold ${mood === i ? 'nav-active' : 'glass-soft'}`}
              data-testid={`mood-${i}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-7">
        <div className="flex-1 basis-56 flex flex-col gap-2.5">
          <div className="flex justify-between items-baseline">
            <label htmlFor="pain" className="label-text text-[var(--text-muted)]">Ból</label>
            <span className="big-num text-3xl">{pain}<span className="text-sm text-[var(--text-muted)] font-bold"> / 10</span></span>
          </div>
          <input id="pain" type="range" min="0" max="10" step="1" value={pain} onChange={(e) => setPain(Number(e.target.value))}
            className="w-full h-7 accent-[var(--text-primary)]" data-testid="pain-slider" />
          <div className="flex justify-between text-[11px] text-[var(--text-muted)]"><span>brak</span><span>bardzo silny</span></div>
        </div>
        <div className="flex-1 basis-44 flex flex-col gap-2.5">
          <p className="label-text text-[var(--text-muted)]">Sen ostatniej nocy</p>
          <div className="flex items-center justify-between gap-3">
            <button onClick={() => setSleep((s) => Math.max(0, s - 0.5))} aria-label="Mniej snu" className="glass-soft w-[52px] h-[52px] rounded-full flex items-center justify-center"><Minus className="w-5 h-5" /></button>
            <span className="big-num text-4xl" data-testid="sleep-value">{sleep.toFixed(1).replace('.', ',')}<span className="text-sm text-[var(--text-muted)] font-bold"> h</span></span>
            <button onClick={() => setSleep((s) => Math.min(14, s + 0.5))} aria-label="Więcej snu" className="glass-soft w-[52px] h-[52px] rounded-full flex items-center justify-center"><Plus className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="cta-gold w-full h-14 rounded-full text-[13px] font-bold tracking-[0.2em] uppercase disabled:opacity-60" data-testid="checkin-save">
        {saving ? 'Zapisywanie...' : 'Zapisz'}
      </button>
    </section>
  );
};

export const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profile, plans, monitoring, workouts, loading, reload } = useClientData();

  useEffect(() => {
    if (user && user.role !== 'client') navigate('/dashboard');
  }, [user, navigate]);

  const today = todayISO();
  const todayEntry = monitoring.find((m) => m.log_date === today) || null;
  const plan = activePlanOf(plans);
  const workout = useMemo(() => {
    if (!plan) return null;
    const day = nextWorkoutDay(plan, workouts);
    if (day == null) return null;
    const exercises = plan.exercises.filter((e) => e.day_number === day).sort((a, b) => a.order_index - b.order_index);
    const doneToday = workouts.some((w) => w.plan_id === plan.id && w.log_date === today);
    return { day, exercises, doneToday };
  }, [plan, workouts, today]);

  const firstName = (profile?.name || user?.name || '').split(' ')[0];
  const dateLabel = new Date().toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' });

  // In the workout card on wider screens, pinned above the tab bar on phones.
  const startButton = (className, testId) => workout && (
    <button onClick={() => navigate(`/client-plan/${plan.id}/trening/${workout.day}`)}
      className={`cta-primary w-full rounded-full text-[13px] font-bold tracking-[0.2em] uppercase items-center justify-center gap-3 ${className}`}
      data-testid={testId}>
      <Play className="w-5 h-5 fill-current" /> {workout.doneToday ? 'Zrób kolejny trening' : 'Zacznij trening'}
    </button>
  );

  return (
    <ClientShell action={!loading && startButton('flex h-14', 'start-today-workout-pinned')}>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 flex flex-col gap-6 sm:gap-8" data-testid="client-dashboard">
        <div className="flex flex-col gap-3">
          <p className="label-text text-[var(--text-muted)]">{dateLabel}</p>
          <h1 className="big-num text-[40px] sm:text-6xl" data-testid="client-greeting">Cześć{firstName ? `, ${firstName}` : ''}</h1>
        </div>

        <ReportDueCard />

        {loading ? (
          <div className="text-[var(--text-muted)] py-12">Ładowanie...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 items-stretch">
            <CheckIn todayEntry={todayEntry} onSaved={reload} />

            <section className="glass rounded-[28px] p-6 sm:p-9 flex flex-col gap-4 relative overflow-hidden" data-testid="today-workout">
              <span aria-hidden="true" className="absolute -right-16 -top-20 w-64 h-64 rounded-full border border-[#C9A84C]/25" />
              <span aria-hidden="true" className="absolute -right-5 -top-8 w-40 h-40 rounded-full border border-[#C9A84C]/20" />
              <p className="label-text text-[#C9A84C]">Trening na dziś</p>
              {!workout ? (
                <>
                  <h2 className="big-num text-3xl sm:text-4xl">Brak aktywnego planu</h2>
                  <p className="text-[var(--text-muted)] font-light">Trener przygotowuje Twój plan. Zajrzyj tu wkrótce.</p>
                </>
              ) : (
                <>
                  <h2 className="big-num text-3xl sm:text-5xl relative">{dayLabel(plan, workout.day)}</h2>
                  <p className="text-[var(--text-muted)] font-light">
                    {plan.plan_name} · {workout.exercises.length} ćwiczeń
                    {workout.doneToday && ' · dzisiejszy trening już zrobiony'}
                  </p>
                  <ol className="hidden sm:flex flex-col gap-2 text-sm text-[var(--text-muted)] mt-1">
                    {workout.exercises.slice(0, 4).map((e, i) => <li key={i}>{i + 1} · {e.exercise_name}</li>)}
                    {workout.exercises.length > 4 && <li className="opacity-70">+ {workout.exercises.length - 4} kolejne</li>}
                  </ol>
                  {startButton('hidden sm:flex mt-auto h-16', 'start-today-workout')}
                  <button onClick={() => navigate(`/client-plan/${plan.id}/dziennik/${workout.day}`)}
                    className="cta-ghost w-full h-12 rounded-full text-xs font-semibold tracking-[0.2em] uppercase flex items-center justify-center gap-2.5"
                    data-testid="open-journal">
                    <NotebookPen className="w-4 h-4" /> Wpisz trening (bez timera)
                  </button>
                </>
              )}
            </section>
          </div>
        )}
      </main>
    </ClientShell>
  );
};
